import React, { useEffect, useState } from 'react';

import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import { fetchSurveyConversion } from './api';
import {
  AccountsMultiSelect,
  ConversionChart,
  DubLinksTable,
  ParentAccountSelect,
} from './components';
import { ParentAccountOption, SurveyConversionResponse } from './types';

const DEFAULT_DAYS = 30;
const MAX_RANGE_DAYS = 60;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const defaultEndDate = (): string => formatLocalDate(new Date());

const defaultStartDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - DEFAULT_DAYS);
  return formatLocalDate(date);
};

const daysBetween = (start: string, end: string): number =>
  Math.round((new Date(end).getTime() - new Date(start).getTime()) / MS_PER_DAY);

const getRangeError = (start: string, end: string, today: string): string | null => {
  if (!start || !end) {
    return 'Select a start and end date.';
  }
  if (start > end) {
    return 'Start date cannot be after end date.';
  }
  if (end > today) {
    return 'End date cannot be in the future.';
  }
  if (daysBetween(start, end) > MAX_RANGE_DAYS) {
    return `Date range cannot exceed ${MAX_RANGE_DAYS} days.`;
  }
  return null;
};

const SurveyConversion: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parentFromUrl = (): ParentAccountOption | null => {
    const id = searchParams.get('parent');
    if (!id) {
      return null;
    }
    return { id, name: searchParams.get('parentName') ?? id };
  };

  const accountIdsFromUrl = (): string[] => {
    const raw = searchParams.get('accounts');
    return raw ? raw.split(',').filter(Boolean) : [];
  };

  const [parent, setParent] = useState<ParentAccountOption | null>(parentFromUrl);
  const [accountIds, setAccountIds] = useState<string[]>(accountIdsFromUrl);
  const [startDate, setStartDate] = useState<string>(
    searchParams.get('start') || defaultStartDate()
  );
  const [endDate, setEndDate] = useState<string>(searchParams.get('end') || defaultEndDate());
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [data, setData] = useState<SurveyConversionResponse | null>(null);

  const today = defaultEndDate();
  const rangeError = getRangeError(startDate, endDate, today);

  // Keep the URL in sync with the current filters so the view is shareable and
  // survives a page reload (the values are read back on mount).
  useEffect(() => {
    const params: Record<string, string> = {};
    if (parent?.id) {
      params.parent = parent.id;
      params.parentName = parent.name;
    }
    if (accountIds.length) {
      params.accounts = accountIds.join(',');
    }
    if (startDate) {
      params.start = startDate;
    }
    if (endDate) {
      params.end = endDate;
    }
    setSearchParams(params, { replace: true });
  }, [parent, accountIds, startDate, endDate, setSearchParams]);

  const { isLoading, isError, isFetching, refetch } = useQuery(
    ['surveyConversion', { parentId: parent?.id, accountIds, startDate, endDate }],
    fetchSurveyConversion,
    {
      enabled: Boolean(parent?.id) && !rangeError,
      refetchOnWindowFocus: false,
      onSuccess: (response) => setData(response.data),
    }
  );

  const handleParentChange = (value: ParentAccountOption | null): void => {
    // Re-selecting the same parent is a no-op: clearing state here would drop
    // the cached chart without re-triggering the query (same query key).
    if (value?.id === parent?.id) {
      return;
    }
    setParent(value);
    setAccountIds([]);
    setSelectedDay(null);
    setData(null);
  };

  const handleDayClick = (day: string): void => {
    setSelectedDay((current) => (current === day ? null : day));
  };

  const isBusy = Boolean(parent?.id) && (isLoading || isFetching);

  return (
    <div className="sm:px-6 lg:px-8 flex flex-col h-full pt-8 pb-12 overflow-x-auto overflow-y-auto">
      <h2 className="sm:px-0 px-4">Survey conversion</h2>
      <p className="sm:px-0 mt-1 px-4 text-gray-3.0">
        Visits vs answered surveys and conversion rate.
      </p>

      <div className="sm:px-0 flex flex-wrap items-end gap-4 px-4 mt-4">
        <div className="min-w-[200px] flex-1">
          <p className="input-label">Parent account</p>
          <ParentAccountSelect value={parent} onChange={handleParentChange} />
        </div>
        <div className="min-w-[200px] flex-1">
          <p className="input-label">Accounts</p>
          <AccountsMultiSelect
            options={data?.accounts ?? []}
            value={accountIds}
            onChange={setAccountIds}
            isDisabled={!parent?.id || !data}
          />
        </div>
        <div>
          <p className="input-label">From</p>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(event) => {
              setStartDate(event.target.value);
              setSelectedDay(null);
            }}
            className="border-gray-2.0 focus:border-primary-2.0 rounded border px-3 py-2 text-sm"
          />
        </div>
        <div>
          <p className="input-label">To</p>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={today}
            onChange={(event) => {
              setEndDate(event.target.value);
              setSelectedDay(null);
            }}
            className="border-gray-2.0 focus:border-primary-2.0 rounded border px-3 py-2 text-sm"
          />
        </div>
        <button
          type="button"
          disabled={!parent?.id || isBusy || Boolean(rangeError)}
          onClick={() => refetch()}
          className="mb-0.5 w-36 bg-primary-2.0 hover:bg-primary-3.0 rounded px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
        >
          {isBusy ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {parent?.id && rangeError && <p className="sm:px-0 error-text px-4 mt-2">{rangeError}</p>}

      {!parent?.id && (
        <p className="sm:px-0 px-4 mt-8 text-gray-3.0">
          Select a parent account to see its survey conversion.
        </p>
      )}

      {isError && (
        <p className="sm:px-0 error-text px-4 mt-8">
          There was an error loading the survey conversion data.
        </p>
      )}

      {parent?.id && !isError && (
        <div className="sm:px-0 px-4 mt-6">
          {isBusy && !data ? (
            <p className="py-12 text-center text-gray-3.0">Loading...</p>
          ) : (
            <>
              <div className="bg-white-1.0 relative p-4 rounded">
                <ConversionChart
                  data={data?.series ?? []}
                  selectedDay={selectedDay}
                  onDayClick={handleDayClick}
                />
                {isBusy && (
                  <div className="opacity-50 bg-white-1.0 absolute inset-0 z-10 flex items-center justify-center rounded flex-col">
                    <div className="border-gray-2.0 border-t-primary-2.0 h-8 w-8 animate-spin rounded-full border-4" />
                    <p className="text-gray-3.0 text-sm">Loading…</p>
                  </div>
                )}
              </div>
              {selectedDay ? (
                <>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-8">
                    <h3>Survey links conversion (Dub) — {selectedDay}</h3>
                    <button
                      type="button"
                      onClick={() => setSelectedDay(null)}
                      className="text-primary-2.0 text-sm underline"
                    >
                      Close
                    </button>
                  </div>
                  <div className="bg-white-1.0 mt-2 rounded">
                    <DubLinksTable links={data?.links ?? []} selectedDay={selectedDay} />
                  </div>
                </>
              ) : (
                <p className="mt-6 text-sm text-center text-gray-3.0">
                  Click a day in the chart to see its survey links (Dub).
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyConversion;
